import { markersV1 } from './markers.v1.js';
import { explicitRulesV1, synthRangeRules, synthRequiredRules } from './rules.v1.js';
import { traitsV1 } from './traits.v1.js';
import { ProtocolSpec } from './types.js';

/**
 * Especificacao executavel do protocolo v1.0.
 *
 * Esta e a fonte unica da verdade do sistema. Alimenta quatro consumidores:
 *   1. a renderizacao do formulario no app;
 *   2. a validacao em campo, offline;
 *   3. o job noturno de qualidade no servidor;
 *   4. o gerador da ficha impressa em PDF.
 *
 * Papel e app nunca divergem porque saem do mesmo arquivo.
 */
export const specV1: ProtocolSpec = ProtocolSpec.parse({
  code: 'v1.0',
  publishedAt: null,
  status: 'rascunho',
  traits: traitsV1,
  markers: markersV1,
  // Ordem deterministica: sintetizadas (na ordem do catalogo) e depois explicitas.
  // O hash depende disso.
  rules: [...synthRequiredRules(traitsV1), ...synthRangeRules(traitsV1), ...explicitRulesV1],
});

/**
 * Serializacao canonica: chaves ordenadas, sem espacos. Duas maquinas produzem
 * byte a byte a mesma string, logo o mesmo hash.
 */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
}

/** SHA-256 da especificacao. Web Crypto: funciona no navegador e no Node >= 20. */
export async function computeSpecHash(spec: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(stableStringify(spec));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Hash congelado da v1.0. Aparece no rodape do app e em todo laudo em PDF —
 * auditabilidade e requisito do fiador cientifico.
 *
 * Se um teste falhar aqui, a especificacao mudou. Isso e de proposito: e o
 * portao que obriga a revisao a duas pessoas antes de alterar o protocolo.
 * Para atualizar depois de uma mudanca aprovada: rode os testes. O teste do
 * hash imprime o valor recebido; cole-o aqui no mesmo commit da mudanca.
 */
export const SPEC_V1_SHA256 = 'c35e709fbfbc29710cfc4eee2f59499f1a31e5e41a0d8020dfc119e3d1ff3c8f';

/**
 * Id fixo da v1.0 no banco. Fica FORA da spec (o hash nao muda) e existe para o
 * tablet criar visita e avaliacao offline: `protocol_version_id` e obrigatorio e
 * o aparelho nunca recebe a tabela de protocolo pelo sync. O seed insere a v1.0
 * com este id; nunca troque o valor depois de haver dado coletado.
 */
export const PROTOCOL_V1_ID = '01926a4c-0000-7000-8000-00000000a010';
